import React, { useState } from "react";
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaPaperPlane,
  FaCheckCircle,
  FaWhatsapp,
} from "react-icons/fa";
import { motion } from "framer-motion";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    visaType: "",
    message: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const whatsappNumber = "03135993810"; // Removed hyphen for URL encoding
  const countryCode = "92"; // Pakistan country code

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    // Create WhatsApp message
    const message = `*New Contact Form Submission - Next Step Consultant*%0A%0A
*Name:* ${formData.name}%0A
*Email:* ${formData.email}%0A
*Phone:* ${formData.phone}%0A
*Visa Type:* ${formData.visaType}%0A
*Message:* ${formData.message}%0A%0A
_I submitted this through your website contact form._`;

    // Create WhatsApp URL
    const whatsappURL = `https://wa.me/${countryCode}${whatsappNumber}?text=${message}`;

    // Open WhatsApp in a new tab
    window.open(whatsappURL, "_blank");

    // Set submitted state
    setIsSubmitted(true);

    // Reset form after a short delay
    setTimeout(() => {
      setFormData({
        name: "",
        email: "",
        phone: "",
        visaType: "",
        message: "",
      });
      setIsSubmitted(false);
      setLoading(false);
    }, 3000);
  };

  const contactInfo = [
    {
      icon: <FaPhone className="text-2xl" />,
      title: "Phone",
      details: ["0313-5993810", "051-2152050"],
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: <FaWhatsapp className="text-2xl" />,
      title: "WhatsApp",
      details: ["+92 313 5993810"],
      color: "bg-green-100 text-green-600",
      whatsapp: true,
      link: `https://wa.me/923135993810`,
    },
    {
      icon: <FaEnvelope className="text-2xl" />,
      title: "Email",
      details: ["nextstepconsultant91@gmail.com"],
      color: "bg-purple-100 text-purple-600",
      email: true,
      link: "mailto:nextstepconsultant91@gmail.com",
    },
    {
      icon: <FaMapMarkerAlt className="text-2xl" />,
      title: "Office",
      details: [
        "Rose Arcade Plaza 3rd floor, Office no. 5",
        "G-11 Markaz, Islamabad",
      ],
      color: "bg-orange-100 text-orange-600",
    },
    {
      icon: <FaClock className="text-2xl" />,
      title: "Business Hours",
      details: ["Mon-Fri: 9:00 AM - 7:00 PM", "Sat: 10:00 AM - 4:00 PM"],
      color: "bg-yellow-100 text-yellow-600",
    },
  ];

  const visaTypes = [
    "Tourist Visa",
    "Student Visa",
    "Schengen Visa",
    "Family Visa",
    "Business Visa",
    "Work Visa",
    "Medical Visa",
    "Other",
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-900 to-primary-700 text-white py-20">
        <div className="container-padding text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl font-bold mb-6"
          >
            Contact Us
          </motion.h1>
          <p className="text-xl max-w-3xl mx-auto text-primary-100">
            Get in touch with our visa experts for personalized assistance
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
            <FaWhatsapp className="text-green-300" />
            <span>Fast response via WhatsApp: +92 313 5993810</span>
          </div>
        </div>
      </section>

      <div className="container-padding py-20">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Get in Touch
            </h2>

            <p className="text-gray-600 mb-12">
              Have questions about visa requirements or need assistance with
              your application? Our team of experts is here to help you every
              step of the way.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={info.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow ${
                    info.whatsapp || info.email
                      ? "cursor-pointer hover:scale-[1.02] transition-transform"
                      : ""
                  }`}
                  onClick={() => {
                    if (info.link) {
                      window.open(info.link, info.whatsapp ? "_blank" : "_self");
                    }
                  }}
                >
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${info.color}`}
                  >
                    {info.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {info.title}
                    {(info.whatsapp || info.email) && (
                      <span className="ml-2 text-sm font-normal text-primary-600">
                        (Click to connect)
                      </span>
                    )}
                  </h3>
                  {info.details.map((detail, i) => (
                    <p key={i} className="text-gray-600">
                      {detail}
                    </p>
                  ))}
                </motion.div>
              ))}
            </div>

            {/* Direct WhatsApp CTA */}
            <div className="mb-8 bg-gradient-to-r from-green-50 to-white rounded-xl p-6 border border-green-200">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <FaWhatsapp className="text-2xl text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Quick WhatsApp Support
                  </h3>
                  <p className="text-gray-600">
                    Get instant answers to your visa questions
                  </p>
                </div>
              </div>
              <a
                href={`https://wa.me/${countryCode}${whatsappNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition"
              >
                <FaWhatsapp className="text-xl" />
                Chat on WhatsApp Now
              </a>
            </div>

            {/* Map Placeholder */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="h-64 bg-gradient-to-r from-primary-100 to-primary-200 flex items-center justify-center">
                <div className="text-center">
                  <FaMapMarkerAlt className="text-4xl text-primary-600 mx-auto mb-4" />
                  <p className="text-gray-700">
                    Rose Arcade Plaza 3rd floor, Office no. 5
                    <br />
                    G-11 Markaz, Islamabad
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Send us a Message
                  </h3>
                  <p className="text-gray-600">
                    Fill out the form below and we'll connect with you on
                    WhatsApp
                  </p>
                </div>
                <FaWhatsapp className="text-3xl text-green-500" />
              </div>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                    <FaCheckCircle className="text-4xl text-green-600" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">
                    Redirecting to WhatsApp!
                  </h4>
                  <p className="text-gray-600 mb-6">
                    Your message is ready to send on WhatsApp. Please click
                    "Send" in WhatsApp to complete.
                  </p>
                  <div className="inline-flex items-center gap-2 text-green-600 font-medium">
                    <FaWhatsapp />
                    <span>WhatsApp is opening...</span>
                  </div>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                        placeholder="+92 313 5993810"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Visa Type *
                    </label>
                    <select
                      name="visaType"
                      value={formData.visaType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                    >
                      <option value="">Select Visa Type</option>
                      {visaTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                      placeholder="Tell us about your visa requirements, destination, timeline, and any specific concerns..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full btn-primary flex items-center justify-center gap-2 ${
                      loading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {loading ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Preparing WhatsApp...
                      </>
                    ) : (
                      <>
                        <FaWhatsapp className="text-xl" />
                        Send via WhatsApp
                      </>
                    )}
                  </button>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800 text-center">
                      <strong>Note:</strong> You'll be redirected to WhatsApp
                      where your message will be ready to send. Please click
                      "Send" in WhatsApp to complete your submission.
                    </p>
                  </div>
                </form>
              )}
            </div>

            {/* FAQ Preview */}
            <div className="mt-12 bg-gradient-to-r from-primary-50 to-white rounded-2xl p-8">
              <h4 className="text-xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h4>
              <div className="space-y-4">
                {[
                  "How long does visa processing take?",
                  "What documents do I need?",
                  "Can I apply for multiple visas?",
                  "Do you offer emergency services?",
                ].map((question, index) => (
                  <div
                    key={index}
                    className="flex items-center text-gray-700 hover:text-primary-600 transition"
                  >
                    <span className="w-2 h-2 bg-primary-600 rounded-full mr-3"></span>
                    {question}
                  </div>
                ))}
              </div>
              <a
                href="/faq"
                className="inline-block mt-6 text-primary-600 font-medium hover:text-primary-700 transition"
              >
                View all FAQs →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;